import React, { useMemo, useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient.ts";

/***********************************
 * GoalPath – Beta V1.6.6
 * - FIX: define computePeriodGoal & computePeriodProfitGoal (ReferenceError)
 * - CHANGE: Dashboard goals are read-only, period-driven (Goal & Profit goal)
 * - CHANGE: Edit defaults only inside modals (Default monthly goal / profit goal)
 * - KEEP: Jobs needed shows "– (-)"; Avg job shows "–" with 0 jobs
 * - KEEP: Recurrence, CSV exports, edit/delete with series support, self-tests
 ***********************************/

/************** UI PRIMITIVES **************/
// Account data comes only from Supabase. Legacy browser snapshots are never
// automatically imported, because their owner cannot be established.
const loadSupabaseState = async (userId: string) => {
  const { data, error } = await supabase.from("app_state")
    .select("state").eq("user_id", userId).maybeSingle();
  if (error) throw error; // A failed read must never become an empty account save.
  return data?.state ?? null;
};

// Preserve write order, including a backup restore following an autosave.
let pendingSave: Promise<void> = Promise.resolve();
const saveSupabaseState = (userId: string, state: any) => {
  const operation = pendingSave.catch(() => {}).then(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id !== userId) throw new Error("Account changed. Save cancelled.");
    const { error } = await supabase.from("app_state").upsert({
      user_id: userId, state, updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
    if (error) throw error;
  });
  pendingSave = operation;
  return operation;
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-black ${className}`}>{children}</div>
);
const CardHeader = ({ children }) => <div className="p-4 border-b border-zinc-100 dark:border-zinc-800">{children}</div>;
const CardContent = ({ children }) => <div className="p-4">{children}</div>;
const Button = ({ children, onClick, className = "", type = "button" }) => (
  <button type={type} onClick={onClick} className={`px-3 py-2 rounded-xl border border-black bg-zinc-50 text-zinc-800 text-sm dark:bg-zinc-800 dark:border-zinc-700 dark:hover:bg-zinc-700 dark:text-zinc-100 ${className}`}>
    {children}
  </button>
);
const PrimaryButton = ({ children, onClick, className = "", type = "button" }) => (
  <button type={type} onClick={onClick} className={`px-3 py-2 rounded-xl bg-emerald-600 text-white hover:opacity-90 text-sm ${className}`}>{children}</button>
);
const Input = ({ className = "", ...props }) => (
  <input
    {...props}
    className={`w-full min-w-[120px] px-3 py-2 rounded-xl border border-zinc-900  focus:outline-none focus:ring-2 focus:ring-emerald-300 dark:bg-zinc-900 dark:border-zinc-700 ${className}`}
  />
);
const Select = ({ value, onChange, children, className = "" }) => (
  <select value={value} onChange={onChange} className={`w-full min-w-[140px] px-3 py-2 rounded-xl border border-zinc-900 dark:bg-zinc-900 dark:border-zinc-900 ${className}`}>{children}</select>
);
const Label = ({ children }) => <label className="text-sm text-zinc-600 dark:text-zinc-300 mb-1 block">{children}</label>;
const Badge = ({ children, tone = "default" }) => {
  const tones = {
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-yellow-100 text-yellow-700",
    default: "bg-blue-100 text-blue-700",
    danger: "bg-red-100 text-red-700",
  };
  return <span className={`px-2 py-1 rounded-lg text-xs ${tones[tone]}`}>{children}</span>;
};
const Modal = ({ open, onClose, title, children, footer }) => open ? (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-black dark:border-zinc-800">
      <div className="flex items-center justify-between p-4 border-b border-zinc-100 dark:border-zinc-800">
        <h3 className="font-semibold">{title}</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-800">✕</button>
      </div>
      <div className="p-4 space-y-3">{children}</div>
      <div className="p-4 border-t border-zinc-900 dark:border-zinc-800 flex justify-end gap-2">{footer}</div>
    </div>
  </div>
) : null;

/************** HELPERS (dates, money) **************/
const currency = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 }).format(n ?? 0);
const toISODateLocal = (d) => { const y=d.getFullYear(); const m=String(d.getMonth()+1).padStart(2,'0'); const day=String(d.getDate()).padStart(2,'0'); return `${y}-${m}-${day}`; };
const parseLocalDate = (s) => { if (!s) return new Date(); const [y,m,d] = s.split('-').map(Number); return new Date(y,(m||1)-1,d||1); };
const todayIso = () => toISODateLocal(new Date());
const toDate = (s) => parseLocalDate(s);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth()+1, 0);
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
const labelMonth = (d) => d.toLocaleString('en-AU', { month: 'short', year: 'numeric' });
const monthsBetweenInclusive = (start, end) => { const s = new Date(start.getFullYear(), start.getMonth(), 1); const e = new Date(end.getFullYear(), end.getMonth(), 1); const out = []; let i=0; const MAX=240; while(s <= e && i<MAX){ out.push(new Date(s)); s.setMonth(s.getMonth()+1); i++; } return out; };
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth()+n, d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate()+n);
const inRange = (date, start, end) => date >= start && date <= end;
const fyRangeAU = (anchor) => { const y=anchor.getMonth()>=6?anchor.getFullYear():anchor.getFullYear()-1; const start=new Date(y,6,1); const end=new Date(y+1,5,30); return { start, end, startYear:y, endYear:y+1 }; };

// CSV helpers
const toCSV = (rows) => { if(!rows||!rows.length) return ''; const headers=Object.keys(rows[0]); const esc=(v)=>'"'+String(v??'').replace(/"/g,'""')+'"'; return [headers.join(',')].concat(rows.map(r=>headers.map(h=>esc(r[h])).join(','))).join('\r\n'); };
const downloadCSV = (rows, filename) => { const csv=toCSV(rows); const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url); };

// domain helpers
const avgJob = (total, count, fallback) => (count>0 ? total / count : fallback);
const computeJobsNeeded = (gap, avg, count) => {
  if (count <= 0 || !isFinite(avg) || avg <= 0) return '–';
  return Math.max(0, Math.ceil(gap / avg));
};
const computePeriodGoal = (months, overridesMap, defaultMonthly) =>
  months.reduce((sum, m) => sum + (overridesMap[monthKey(m)] ?? defaultMonthly), 0);
const computePeriodProfitGoal = (months, overridesMap, defaultMonthly) =>
  months.reduce((sum, m) => sum + (overridesMap[monthKey(m)] ?? defaultMonthly), 0);

const toDateISO = (iso) => new Date(iso + "T00:00:00");

const diffDays = (fromIso, toIso) =>
  Math.round((toDateISO(toIso).getTime() - toDateISO(fromIso).getTime()) / 86400000);

const addDaysIso = (iso, days) => {
  const d = toDateISO(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/************** RECURRENCE **************/
function scheduleOccurrences(startISO, { mode, every=1, unit='weeks', endType='on', count=6, untilDate=null }){
  const start = toDate(startISO);
  const dates = [start];
  if(mode==='none') return dates.map(d=>toISODateLocal(d));
  if(mode==='weekly'){ every=1; unit='weeks'; }
  if(mode==='fortnightly'){ every=2; unit='weeks'; }
  if(mode==='monthly'){ every=1; unit='months'; }
  const MAX_GEN=200; let i=1;
  while(true){
    const prev = dates[dates.length-1];
    let next;
    if(unit==='weeks') next = addDays(prev, 7*every);
    else if(unit==='months') next = addMonths(prev, every);
    else next = addDays(prev, every);
    if(endType==='on' && untilDate){ if(next > toDate(untilDate)) break; }
    if(endType==='after'){ if(i>=count) break; }
    if(endType==='never' && dates.length>=MAX_GEN) break;
    dates.push(next); i++;
  }
  return dates.map(d=>toISODateLocal(d));
}

function RepeatControls({ repeat, setRepeat, fyEndIso }){
  return (
    <Card className="mt-3">
      <CardHeader><div className="font-semibold">Repeat</div></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* Frequency (mode) */}
          <div className="min-w-[180px]">
            <Select value={repeat.mode} onChange={e=>setRepeat({ ...repeat, mode: e.target.value })} className="w-full">
              <option value="none">Does not repeat</option>
              <option value="weekly">Once a week</option>
              <option value="fortnightly">Once a fortnight</option>
              <option value="monthly">Once a month</option>
              <option value="custom">Custom…</option>
            </Select>
          </div>

          {/* Interval (only for custom) */}
          <div>
            {repeat.mode==='custom' && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm">every</span>
                <Input type="number" className="w-24" value={repeat.every} onChange={e=>setRepeat({ ...repeat, every:Number(e.target.value||1) })} />
                <Select value={repeat.unit} onChange={e=>setRepeat({ ...repeat, unit:e.target.value })} className="min-w-[130px]">
                  <option value="days">days</option>
                  <option value="weeks">weeks</option>
                  <option value="months">months</option>
                </Select>
              </div>
            )}
          </div>

          {/* End condition */}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={repeat.endType} onChange={e=>setRepeat({ ...repeat, endType: e.target.value })} className="min-w-[130px]">
                <option value="after">After</option>
                <option value="on">On date</option>
                <option value="never">Never</option>
              </Select>
              {repeat.endType==='after' && (
                <Input type="number" className="w-28" value={repeat.count} onChange={e=>setRepeat({ ...repeat, count:Number(e.target.value||1) })} placeholder="occurrences" />
              )}
              {repeat.endType==='on' && (
                <Input type="date" className="min-w-[200px]" value={repeat.untilDate || fyEndIso} onChange={e=>setRepeat({ ...repeat, untilDate: e.target.value })} />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/************** Tables & KPIs **************/
function Stat({ title, value }){
  return (
    <Card>
      <CardContent>
        <div className="text-xs text-zinc-500">{title}</div>
        <div className="text-xl font-semibold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

/************** Action Row (edit/delete) **************/
function ActionRow({ record, onApply, onDelete, scoped, jobTypes = [] }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('one');
  const [draft, setDraft] = useState(record);

  const isExpense = draft && Object.prototype.hasOwnProperty.call(draft, 'supplier');

  const openEdit = () => {
    setDraft({ ...record });
    setOpen(true);
  };

  return (
    <>
      <div className="flex gap-2">
        <Button onClick={openEdit}>Edit</Button>
        <Button
          onClick={() => {
            setScope('one');
            onDelete('one');

          }}
        >
          Delete
        </Button>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={isExpense ? 'Edit expense' : 'Edit invoice'}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Close</Button>

            <PrimaryButton
              onClick={() => {
                onApply({ ...draft, _baseDate: record.date }, scope);
                setOpen(false);
              }}
            >
              Save changes
            </PrimaryButton>

            {scoped && (
              <div style={{ marginTop: 8 }}>
                <Label>Apply changes to</Label>
                <Select value={scope} onChange={e => setScope(e.target.value)}>
                  <option value="one">Only this item</option>
                  <option value="following">This &amp; following items</option>
                  <option value="series">All in this series</option>
                </Select>
              </div>
            )}
          </>
        }
      >
        {!scoped && (
          <div className="text-sm text-zinc-500">Editing this single item.</div>
        )}
        
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
       {!isExpense && (
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={draft.date}
              onChange={e => setDraft({ ...draft, date: e.target.value })}
            />
          </div>
        )}

          {isExpense ? (
  <>
    <div>
      <Label>Date</Label>
      <Input
        type="date"
        value={draft.date}
        onChange={e => setDraft({ ...draft, date: e.target.value })}
      />
    </div>

    <div>
      <Label>Supplier</Label>
      <Input
        value={draft.supplier || ''}
        onChange={e => setDraft({ ...draft, supplier: e.target.value })}
      />
    </div>

    <div>
      <Label>GST</Label>
      <Input
        type="number"
        value={draft.gst || 0}
        onChange={e => setDraft({ ...draft, gst: Number(e.target.value) })}
      />
    </div>

    <div>
      <Label>Status</Label>
      <Select
        value={draft.status || "Draft"}
        onChange={e => setDraft({ ...draft, status: e.target.value })}
      >
        <option>Draft</option>
        <option>Paid</option>
      </Select>
    </div>

    <div className="md:col-span-2">
      <Label>Total (incl.)</Label>
      <Input
        type="number"
        value={draft.total}
        onChange={e => setDraft({ ...draft, total: Number(e.target.value) })}
      />
    </div>

    <div className="md:col-span-2 flex items-center gap-2">
      <input
        type="checkbox"
        checked={draft.personal}
        onChange={e => setDraft({ ...draft, personal: e.target.checked })}
      />
      <span>Mark as personal (excluded)</span>
    </div>
  </>
) : (
            <>
              <div>
                <Label>Customer</Label>
                <Input
                  value={draft.customer || ''}
                  onChange={e => setDraft({ ...draft, customer: e.target.value })}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Select
                  value={draft.jobType || ''}
                  onChange={e => setDraft({ ...draft, jobType: e.target.value })}
                >
                  <option value="">—</option>
                  {[...jobTypes]
                  .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                  .map(t => (
                  <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={draft.status}
                  onChange={e => setDraft({ ...draft, status: e.target.value })}
                >
                  <option>Draft</option>
                  <option>Sent</option>
                  <option>Paid</option>
                </Select>
              </div>
            </>
          )}

          <div>
            <Label>Total (incl.)</Label>
            <Input
              type="number"
              value={draft.total}
              onChange={e =>
                setDraft({ ...draft, total: Number(e.target.value || 0) })
              }
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

/************** APP **************/
export default function App(){
const [user, setUser] = useState<any>(null);
const accountRef = useRef<string | null>(null);
const authVersionRef = useRef(0);
const [loadedAccountId, setLoadedAccountId] = useState<string | null>(null);
const [accountLoadError, setAccountLoadError] = useState("");
const acceptUser = (nextUser: any) => {
  const nextId = nextUser?.id ?? null;
  if (accountRef.current !== nextId) {
    accountRef.current = nextId;
    authVersionRef.current += 1;
    setHasLoadedSupabase(false);
    setLoadedAccountId(null);
    setAccountLoadError("");
    setHasSubscriptionAccess(false);
    setSubscriptionLoading(Boolean(nextUser));
    setInvoices([]); setExpenses([]); setCustomers([]); setSuppliers([]);
    setJobTypes([]); setGoal(0); setGoalsByMonth({});
    setProfitGoal(0); setProfitGoalsByMonth({}); setIncludeProjected(true);
    setInvOpen(false); setExpOpen(false); setGoalsOpen(false); setProfitGoalsOpen(false);
    setInvoiceFilter(""); setExpenseFilter(""); setMsg("");
    setInvForm({ date: todayIso(), customer: '', jobType: '', total: 0, status: 'Draft' });
    setExpForm({ date: todayIso(), supplier: '', total: "", gst: 0, personal: false, status: "Draft" });
    setSaveStatus(""); setLastSynced("");
  }
  setUser(nextUser);
};
const [loading, setLoading] = useState(true);
const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(false);
const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const today = new Date();
  const { start: fyStart, end: fyEnd, startYear, endYear } = fyRangeAU(today);
  const fyEndIso = toISODateLocal(fyEnd);

  const [newPassword, setNewPassword] = useState("");
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  useEffect(() => {
  async function getUser() {
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get("token_hash");
    const verificationType = params.get("type");

    if (tokenHash && verificationType) {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: verificationType as any
      });

      if (error) {
        setAuthMessage(`Email confirmation failed: ${error.message}`);
      } else {
        window.history.replaceState(null, "", window.location.pathname);
      }
    }

    const version = authVersionRef.current;
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (version !== authVersionRef.current) { setLoading(false); return; }

    acceptUser(session?.user ?? null);
    setLoading(false);  
  }

  getUser();

  const {
    data: { subscription }
  } = supabase.auth.onAuthStateChange((event, session) => {
  acceptUser(session?.user ?? null);

  if (event === "PASSWORD_RECOVERY") {
    setIsPasswordRecovery(true);
  }
});

  return () => subscription.unsubscribe();
}, []);
  useEffect(() => {
  let cancelled = false;

  async function checkSubscriptionAccess() {
    if (!user?.email) {
      setHasSubscriptionAccess(false);
      setSubscriptionLoading(false);
      return;
    }

    setSubscriptionLoading(true);

    const { data, error } = await supabase
      .from("subscription_access")
      .select("status")
      .eq("email", user.email.toLowerCase())
      .maybeSingle();

    if (cancelled || accountRef.current !== user.id) return;

    if (error) {
      console.error("Subscription access check failed:", error);
      setHasSubscriptionAccess(false);
    } else {
      setHasSubscriptionAccess(
        data?.status === "active" || data?.status === "trialing"
      );
    }

    setSubscriptionLoading(false);
  }

  checkSubscriptionAccess();

  return () => {
    cancelled = true;
  };
}, [user]);
    useEffect(() => {
      const goOnline = () => setIsOnline(true);
      const goOffline = () => setIsOnline(false);

      window.addEventListener("online", goOnline);
      window.addEventListener("offline", goOffline);

      return () => {
        window.removeEventListener("online", goOnline);
        window.removeEventListener("offline", goOffline);
      };
    }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [authScreen, setAuthScreen] = useState<'login' | 'signup'>('login');
  const [authMessage, setAuthMessage] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [lastSynced, setLastSynced] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // toggles & goals
  const [includeProjected, setIncludeProjected] = useState(true);
  const [goal, setGoal] = useState(0); // default monthly revenue goal
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [goalsByMonth, setGoalsByMonth] = useState({});
  const [profitGoal, setProfitGoal] = useState(0); // default monthly profit goal
  const [profitGoalsOpen, setProfitGoalsOpen] = useState(false);
  const [profitGoalsByMonth, setProfitGoalsByMonth] = useState({});

  // period controls
  const thisStart = startOfMonth(today), thisEnd=endOfMonth(today);
  const [period, setPeriod] = useState('this');
  const [customStart, setCustomStart] = useState(toISODateLocal(thisStart));
  const [customEnd, setCustomEnd] = useState(toISODateLocal(thisEnd));
  const range = period==='this'? {start:thisStart,end:thisEnd} : period==='fy'? {start:fyStart,end:fyEnd} : {start:toDate(customStart), end:toDate(customEnd)};

  // data
  const [hasLoadedSupabase, setHasLoadedSupabase] = useState(false);

  const [invoices, setInvoices] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<string[]>([]);

// Job Types (Income only)

const [jobTypes, setJobTypes] = useState<string[]>([]);
  // repeat defaults
  const [repeatInv, setRepeatInv] = useState({ mode:'none', every:2, unit:'weeks', endType:'on', count:6, untilDate: fyEndIso });
  const [repeatExp, setRepeatExp] = useState({ mode:'none', every:1, unit:'months', endType:'on', count:12, untilDate: fyEndIso });
  useEffect(() => {
    let cancelled = false;
    const owner = user?.id;
    const version = authVersionRef.current;
    const isCurrent = () => !cancelled && accountRef.current === owner &&
      authVersionRef.current === version;
    if (!owner || !hasSubscriptionAccess) return;
    async function loadAccount() {
      setHasLoadedSupabase(false);
      setLoadedAccountId(null);
      setAccountLoadError("");
      try {
        const cloudState = (await loadSupabaseState(owner)) ?? {};
        if (!isCurrent()) return;
        setCustomers(Array.isArray(cloudState.customers) ? cloudState.customers : []);
        setSuppliers(Array.isArray(cloudState.suppliers) ? cloudState.suppliers : []);
        setJobTypes(Array.isArray(cloudState.jobTypes) ? cloudState.jobTypes : []);
        setInvoices(Array.isArray(cloudState.invoices) ? cloudState.invoices : []);
        setExpenses(Array.isArray(cloudState.expenses) ? cloudState.expenses : []);
        setGoal(typeof cloudState.goal === "number" ? cloudState.goal : 0);
        setGoalsByMonth(cloudState.goalsByMonth ?? {});
        setProfitGoal(typeof cloudState.profitGoal === "number" ? cloudState.profitGoal : 0);
        setProfitGoalsByMonth(cloudState.profitGoalsByMonth ?? {});
        setIncludeProjected(typeof cloudState.includeProjected === "boolean" ? cloudState.includeProjected : true);
        setLoadedAccountId(owner);
        setHasLoadedSupabase(true);
      } catch (error) {
        if (isCurrent()) setAccountLoadError("Could not load your account. Reload to try again. Your saved data has not been replaced.");
      }
    }
    loadAccount();
    return () => { cancelled = true; };
  }, [user?.id, hasSubscriptionAccess]);
  // derivations
  const periodInvoices = useMemo(()=> invoices.filter(i=> inRange(toDate(i.date), range.start, range.end)), [invoices, period, customStart, customEnd]);
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const filteredInvoices = useMemo(()=> {
    const q = invoiceFilter.trim().toLowerCase();
    if (!q) return periodInvoices;
    return periodInvoices.filter(i => (i.customer||'').toLowerCase().includes(q));
  }, [periodInvoices, invoiceFilter]);
  const periodExpenses = useMemo(()=> 
    expenses.filter(e=> 
      !e.personal &&
      inRange(toDate(e.date), range.start, range.end)
    )
  , [expenses, period, customStart, customEnd]);
  const [expenseFilter, setExpenseFilter] = useState('');
  const filteredExpenses = useMemo(() => {
  const q = expenseFilter.trim().toLowerCase();
  if (!q) return periodExpenses;
  return periodExpenses.filter(e => (e.supplier || '').toLowerCase().includes(q));
  }, [periodExpenses, expenseFilter]);
  const actual = periodInvoices.reduce((s,i)=> s + (i.status!=='Draft'? i.total:0), 0);
  const upcoming = periodInvoices.reduce((s,i)=> s + (i.status==='Draft'? i.total:0), 0);
  const projected = actual + upcoming;
  const revenue = includeProjected ? projected : actual;
  const costTotal = periodExpenses.reduce((s,e)=> s + e.total, 0);
  const profit = revenue - costTotal;
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

  // average job logic
  const includedInvoices = includeProjected ? periodInvoices : periodInvoices.filter(i=> i.status!=='Draft');
  const jobsCount = includedInvoices.length;
  const avgJobValue = avgJob(revenue, jobsCount, goal);

  // goals
  const months = monthsBetweenInclusive(range.start, range.end);
  const periodGoal = computePeriodGoal(months, goalsByMonth, goal);
  const gap = Math.max(0, periodGoal - revenue);
  const jobs = computeJobsNeeded(gap, avgJobValue, jobsCount);

  // profit period goal (sum overrides else default)
  const periodProfitGoal = computePeriodProfitGoal(months, profitGoalsByMonth, profitGoal);
  const profitGap = Math.max(0, periodProfitGoal - profit);

  // CRUD helpers
  const addInvoices = (base, repeat) => {
    const customerName = (base.customer || '').trim();

    if (!customerName) {
      alert("Customer name is required.");
      return;
    }

    const recurId = Date.now();
    const cleanStart = toISODateLocal(toDate(base.date));
    const dates = scheduleOccurrences(cleanStart, repeat);
    const items = dates.map((iso, idx)=> ({ id:recurId+idx, recurrenceId:recurId, occurrenceIndex:idx, date:iso, customer: base.customer||'', jobType: base.jobType||'', total:Number(base.total), status: idx===0? base.status : "Draft" }));
  setInvoices(prev => [...items, ...prev]);

// ✅ Auto-save customer
if (customerName && !customers.includes(customerName)) {
  setCustomers(prev => [...prev, customerName].sort());
}

// ✅ Auto-save job type
const jobTypeName = (base.jobType || '').trim();
if (jobTypeName && !jobTypes.includes(jobTypeName)) {
  setJobTypes(prev => [...prev, jobTypeName].sort());
}
  };
  const addExpenses = (base, repeat) => {
    const recurId = Date.now();
    const cleanStart = toISODateLocal(toDate(base.date));
    const dates = scheduleOccurrences(cleanStart, repeat);
    const items = dates.map((iso, idx)=> ({ 
      id:recurId+idx, 
      recurrenceId:recurId, 
      occurrenceIndex:idx, 
      date:iso, 
      supplier: base.supplier, 
      total:Number(base.total), 
      gst:Number(base.gst||0), 
      personal: !!base.personal, 
      status: idx === 0 ? (base.status || "Draft") : "Draft" 
    }));

    setExpenses(prev=> [...items, ...prev]);
    // ✅ Auto-save supplier
    const supplierName = (base.supplier || '').trim();
    if (supplierName && !suppliers.includes(supplierName)) {
      setSuppliers(prev => [...prev, supplierName].sort());
    }
  };
  const updateInvoice = (rec, scope='one') => {

    const customerName = (rec.customer || "").trim();

    if (!customerName) {
      alert("Customer name is required.");
      return;
    }

    setInvoices(prev => {
      const original = prev.find(i => i.id === rec.id);
      if (!original) return prev;

    // Only this invoice
    if (scope === 'one') {
      return prev.map(i => i.id === rec.id ? { ...rec, total: Number(rec.total) } : i);
    }

    // All in this series (DO NOT change dates)
    if (scope === 'series') {
      return prev.map(i => {
        if (i.recurrenceId !== rec.recurrenceId) return i;
        return {
          ...i,
          customer: customerName,
          jobType: rec.jobType,
          total: Number(rec.total),
          // only change status on the edited row, leave others alone
          status: i.id === rec.id ? rec.status : i.status
        };
      });
    }

    // This & following
    if (scope === 'following') {
      const msPerDay = 24 * 60 * 60 * 1000;
      const oldD = toDate(original.date);
      const newD = toDate(rec.date);
      const deltaDays = Math.round((newD.getTime() - oldD.getTime()) / msPerDay);

      return prev.map(i => {
        if (i.recurrenceId !== rec.recurrenceId) return i;
        if ((i.occurrenceIndex ?? 0) < (original.occurrenceIndex ?? 0)) return i;

        const shiftedDate =
          deltaDays === 0 ? i.date : toISODateLocal(addDays(toDate(i.date), deltaDays));

        return {
          ...i,
          date: shiftedDate,
          customer: customerName,
          jobType: rec.jobType,
          total: Number(rec.total),
          status: i.id === rec.id ? rec.status : i.status
        };
      });
    }

    return prev;
  });
};
  const deleteInvoice = (rec, scope = 'one') => {
  setInvoices(prev => {
    // safety: if it’s not a recurring item, treat as single delete
    if (scope === 'one' || !rec.recurrenceId) {
      return prev.filter(i => i.id !== rec.id);
    }

    if (scope === 'series') {
      return prev.filter(i => i.recurrenceId !== rec.recurrenceId);
    }

    // scope === 'following'
    const cutIndex = rec.occurrenceIndex ?? 0;
    return prev.filter(i => {
      if (i.recurrenceId !== rec.recurrenceId) return true;
      return (i.occurrenceIndex ?? 0) < cutIndex;
    });
  });
};

  const updateExpense = (rec, scope='one') => {

  setExpenses(prev => {
    const original = prev.find(e => e.id === rec.id);
    if (!original) return prev;

    if (scope === 'one') {
      return prev.map(e =>
        e.id === rec.id
        ? { ...rec, total: Number(rec.total), gst: Number(rec.gst), status: rec.status }
        : e
    );
    }

    if (scope === 'series') {
      return prev.map(e => {
        if (e.recurrenceId !== rec.recurrenceId) return e;
        return {
          ...e,
          supplier: rec.supplier,
          total: Number(rec.total),
          gst: Number(rec.gst),
          personal: rec.personal,
          status: rec.status
        };
      });
    }

    if (scope === 'following') {
      const msPerDay = 24 * 60 * 60 * 1000;
      const oldD = toDate(original.date);
      const newD = toDate(rec.date);
      const deltaDays = Math.round((newD.getTime() - oldD.getTime()) / msPerDay);

      return prev.map(e => {
        if (e.recurrenceId !== rec.recurrenceId) return e;
        if ((e.occurrenceIndex ?? 0) < (original.occurrenceIndex ?? 0)) return e;

        const shiftedDate =
          deltaDays === 0 ? e.date : toISODateLocal(addDays(toDate(e.date), deltaDays));

        return {
          ...e,
          date: shiftedDate,
          supplier: rec.supplier,
          total: Number(rec.total),
          gst: Number(rec.gst),
          personal: rec.personal
        };
      });
    }

    return prev;
  });
};

  const deleteExpense = (rec, scope = 'one') => {
  setExpenses(prev => {
    if (scope === 'one' || !rec.recurrenceId) {
      return prev.filter(e => e.id !== rec.id);
    }

    if (scope === 'series') {
      return prev.filter(e => e.recurrenceId !== rec.recurrenceId);
    }

    // scope === 'following'
    const cutIndex = rec.occurrenceIndex ?? 0;
    return prev.filter(e => {
      if (e.recurrenceId !== rec.recurrenceId) return true;
      return (e.occurrenceIndex ?? 0) < cutIndex;
    });
  });
};

  // forms
  const [invOpen, setInvOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [invForm, setInvForm] = useState({ date: todayIso(), customer:'', jobType:'', total: 0, status: 'Draft' });
  const [showAddJobTypeInv, setShowAddJobTypeInv] = useState(false);
  const [newJobTypeInv, setNewJobTypeInv] = useState('');
  const [expForm, setExpForm] = useState({ date: todayIso(), supplier:'', total:"", gst:0, personal:false, status: "Draft" });
  const [msg, setMsg] = React.useState('');

  const exportNow = async () => {
  try {
    if (!user || loadedAccountId !== user.id || accountRef.current !== user.id || !hasLoadedSupabase) {
      throw new Error("Wait for your account to finish loading.");
    }
    const data = { customers, suppliers, invoices, expenses, jobTypes, goal,
      goalsByMonth, profitGoal, profitGoalsByMonth, includeProjected };

    // Electron version
    if (window.backupAPI?.exportBackup) {
      const ok = await window.backupAPI.exportBackup(data);
      if (ok) setMsg('Backup saved successfully.');
      return;
    }

    // Web fallback
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'forecast-backup.json';
    a.click();
    URL.revokeObjectURL(url);

    setMsg('Backup downloaded.');
  } catch (e) {
    console.error(e);
    setMsg('Backup failed.');
  }
};

// Persist restored data to the account before reloading. Otherwise startup
// loads the previous cloud state over the restored browser backup.
const restoreBackup = async (data: any) => {
  if (!data || typeof data !== "object" || Array.isArray(data) ||
      !Array.isArray(data.invoices) || !Array.isArray(data.expenses) ||
      !Array.isArray(data.customers) || !Array.isArray(data.suppliers)) {
    throw new Error("This file is not a valid GoalPath backup.");
  }

  if (!user || !hasSubscriptionAccess || !hasLoadedSupabase || loadedAccountId !== user.id || accountRef.current !== user.id) {
    throw new Error("Wait for your account to finish loading before restoring.");
  }

  setMsg("Restoring backup to your account…");
  const owner = user.id;
  const version = authVersionRef.current;
  await saveSupabaseState(owner, data);
  if (accountRef.current !== owner || authVersionRef.current !== version) return;
  setMsg("Backup restored. Reloading…");
  location.reload();
};

const reportImportError = (error: any) => {
  console.error("Backup restore failed:", error);
  setMsg(`Restore failed: ${error?.message || "Please try again."}`);
};

const importNow = async () => {
  try {
    // Electron version
    if (window.backupAPI?.importBackup) {
      const imported = await window.backupAPI.importBackup();
      if (!imported) {
        setMsg('Import cancelled.');
        return;
      }
      await restoreBackup(imported);
      return;
    }

    // Web fallback: errors inside this callback need their own handler.
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async () => {
      try {
        const file = input.files?.[0];
        if (!file) return;
        const data = JSON.parse(await file.text());
        await restoreBackup(data);
      } catch (error) {
        reportImportError(error);
      }
    };
    input.click();
  } catch (error) {
    reportImportError(error);
  }
};
  // snapshot rows
  const snapshotRows = [
    { Metric:'Revenue', Amount: revenue.toFixed(2) },
    { Metric:'Actual (invoiced)', Amount: actual.toFixed(2) },
    { Metric:'Upcoming (drafts)', Amount: upcoming.toFixed(2) },
    { Metric:'Costs', Amount: costTotal.toFixed(2) },
    { Metric:'Profit', Amount: profit.toFixed(2) },
    { Metric:'Period goal', Amount: periodGoal.toFixed(2) },
    { Metric:'Jobs needed', Amount: jobs },
    { Metric:'Period profit goal', Amount: periodProfitGoal.toFixed(2) },
    { Metric:'Profit goal left', Amount: profitGap.toFixed(2) },
  ];

  const periodLabel = period==='this' ? 'This month' : period==='fy' ? `FY ${startYear}-${String(endYear).slice(-2)}` : `Custom ${customStart} → ${customEnd}`;
  const overridesCount = Object.keys(goalsByMonth).length;
// Save whenever core state changes
React.useEffect(() => {
  const state = {
    customers,
    suppliers,
    invoices,
    expenses,
    jobTypes,
    goal,
    goalsByMonth,
    profitGoal,
    profitGoalsByMonth,
    includeProjected
  };

    if (user && hasSubscriptionAccess && hasLoadedSupabase && loadedAccountId === user.id && accountRef.current === user.id) {
  const owner = user.id;
  const version = authVersionRef.current;
  const isCurrent = () => accountRef.current === owner && authVersionRef.current === version;
  setSaveStatus("Saving...");

  saveSupabaseState(user.id, state)
  .then(() => {
    if (!isCurrent()) return;
    setSaveStatus("Saved");

    setLastSynced(
      new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit"
      })
    );

    setTimeout(() => {
      if (isCurrent()) setSaveStatus("");
    }, 2000);
  })
  .catch(() => {
    if (!isCurrent()) return;
    setSaveStatus("Save failed. Check your internet connection");
  });
}
}, [
  user,
  loadedAccountId,
  hasLoadedSupabase,
  customers,
  suppliers,
  invoices,
  expenses,
  jobTypes,
  goal,
  goalsByMonth,
  profitGoal,
  profitGoalsByMonth,
  hasSubscriptionAccess,
  includeProjected
]);
  if (loading) {
  return <div className="p-8">Loading...</div>;
}
if (user && isPasswordRecovery) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow p-6 w-full max-w-sm space-y-4">
        <h1 className="text-xl font-semibold">Set new password</h1>

        <input
          className="w-full border rounded-xl p-3"
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              setAuthLoading(true);
              setAuthMessage("");

              const { error } = await supabase.auth.signInWithPassword({
                email,
                password
              });

              if (error) {
                setAuthMessage(error.message);
              }

              setAuthLoading(false);
            }
          }}
        />

        <PrimaryButton
          onClick={async () => {
            const { error } = await supabase.auth.updateUser({
              password: newPassword
            });

            if (error) {
              setAuthMessage(error.message);
            } else {
              setAuthMessage("Password updated successfully.");
              setNewPassword("");
              setIsPasswordRecovery(false);
              window.history.replaceState(null, "", window.location.pathname);
              }
            }}
        >
          Update password
        </PrimaryButton>

        {authMessage && (
          <div className="text-sm text-zinc-700 bg-zinc-100 border border-zinc-300 rounded-xl p-3">
            {authMessage}
          </div>
        )}
      </div>
    </div>
  );
}
    if (!user) {
  return (
    <div className="min-h-screen bg-white text-zinc-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h2 className="text-xl font-semibold">GoalPath</h2>
          <p className="text-sm text-zinc-500">
            {authScreen === "login" ? "Welcome back" : "Create your account"}
          </p>
        </CardHeader>

        <CardContent>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setAuthLoading(true);
              setAuthMessage("");

              if (authScreen === "login") {
                const { error } = await supabase.auth.signInWithPassword({
                  email,
                  password,
                });

                if (error) setAuthMessage(error.message);
              } else {
                const { error } = await supabase.auth.signUp({
                  email,
                  password,
                  options: {
                    data: {
                      name,
                      business_name: businessName,
                    },
                }});

                if (error) {
                  setAuthMessage(error.message);
                } else {
                  setAuthMessage(
                    "Your account has been created successfully. Please check your email to confirm your account. Once confirmed, return here and log in."
                  );
                
                  setName("");
                setBusinessName("");
                setEmail("");
                setPassword("");

                setAuthScreen("login");
                }
              }

              setAuthLoading(false);
            }}
          >
            {authScreen === "signup" && (
            <>
              <div>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <Label>Business name (optional) </Label>
                <Input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </div>
            </>
          )}
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={
                  authScreen === "login" ? "current-password" : "new-password"
                }
              />
            </div>
            
            <Button type="submit" className="w-full">
              {authLoading
                ? "Please wait..."
                : authScreen === "login"
                ? "Log in"
                : "Create account"}
            </Button>

            {authScreen === "login" && (
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={async () => {
                  if (!email) {
                    setAuthMessage("Enter your email address first.");
                    return;
                  }

                  setAuthLoading(true);
                  setAuthMessage("");

                  const { error } =
                    await supabase.auth.resetPasswordForEmail(email);

                  if (error) {
                    setAuthMessage(error.message);
                  } else {
                    setAuthMessage("Password reset email sent.");
                  }

                  setAuthLoading(false);
                }}
              >
                Forgot password
              </Button>
            )}

            {authMessage && (
              <div className="text-sm text-zinc-600">{authMessage}</div>
            )}

            <div className="pt-2 text-center text-sm text-zinc-600">
              {authScreen === "login" ? (
                <>
                  Don't have an account?{" "}
                  <button
                    type="button"
                    className="font-medium text-emerald-700 underline"
                    onClick={() => {
                      setAuthScreen("signup");
                      setEmail("");
                      setPassword("");
                      setAuthMessage("");
                    }}
                  >
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="font-medium text-emerald-700 underline"
                    onClick={() => {
                      setAuthScreen("login");
                      setEmail("");
                      setPassword("");
                      setAuthMessage("");
                    }}
                  >
                    Back to log in
                  </button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
if (subscriptionLoading) {
  return <div className="p-8">Checking subscription...</div>;
}

if (!hasSubscriptionAccess) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h2 className="text-xl font-semibold">Subscription required</h2>
          <p className="text-sm text-zinc-500">
            We couldn’t find an active GoalPath subscription or free trial
            for {user?.email}.
          </p>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            <PrimaryButton
              className="w-full"
              onClick={() => {
                window.location.href = "https://goalpathapp.com";
              }}
            >
              Go to GoalPath
            </PrimaryButton>

            <Button
              className="w-full"
              onClick={() => window.location.reload()}
            >
              Check again
            </Button>

            <Button
              className="w-full"
              onClick={() => supabase.auth.signOut()}
            >
              Log out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

if (!hasLoadedSupabase || loadedAccountId !== user.id) {
  return <div className="p-8">
    <p>{accountLoadError || "Loading your account…"}</p>
    {accountLoadError && <button onClick={() => window.location.reload()}>Retry</button>}
    <button className="ml-4" onClick={() => supabase.auth.signOut()}>Log out</button>
  </div>;
}

return (

    <div className="min-h-screen bg-white text-zinc-900">
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-semibold">GoalPath – Beta V1.6.6</div>
            <div className="text-xs text-zinc-500">Actual = invoiced (Sent/Paid) • Upcoming = Drafts • Toggle below</div>
          </div>

          <div className="flex items-center gap-2">
            {!isOnline && (
              <form className="text-sm font-medium text-orange-600">
                Offline — changes can't be saved
              </form>
            )}
            {saveStatus && (
              <div
                className={`text-sm font-medium ${
                  saveStatus === "Saved"
                    ? "text-emerald-600"
                    : saveStatus === "Saving..."
                    ? "text-amber-600"
                    : "text-red-600"
                }`}
              >
                {saveStatus}
              </div>
            )}
                      {lastSynced && (
                        <div className="text-xs text-zinc-500">
                          Last synced: {lastSynced}
                        </div>
                      )}
            <Button onClick={()=>setIncludeProjected(!includeProjected)}>{includeProjected? 'Actual + Upcoming' : 'Actual only'}</Button>
            <PrimaryButton onClick={()=>setInvOpen(true)}>New sale</PrimaryButton>
            <Button onClick={()=>setExpOpen(true)}>New expense</Button>
          </div>
        </div>
          <div className="text-right">
          <div className="text-sm font-medium">
            {user?.email}
          </div>
        </div>
            <Button
              onClick={async () => {
                setAuthMessage("Signing out...");

                const { error } = await supabase.auth.signOut();

                if (error) {
                  setAuthMessage(error.message);
                } else {
                  setAuthMessage("");
                }
              }}
            >
              Logout
            </Button>
        {/* PERIOD & KPIs */}
        <Card>
          <CardContent>{period === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            <div>
                <Label>Start</Label>
                <Input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                />
              </div>

              <div>
                <Label>End</Label>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                />
              </div>
            </div>
          )}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
              <div className="md:col-span-2">
                <Label>Period</Label>
                <div className="flex rounded-xl border overflow-hidden">
                  <button className={`px-3 py-2 text-sm ${period==='this'?'bg-emerald-600 text-white':'bg-zinc-50'}`} onClick={()=>setPeriod('this')}>This month</button>
                  <button className={`px-3 py-2 text-sm ${period==='fy'?'bg-emerald-600 text-white':'bg-zinc-50'}`} onClick={()=>setPeriod('fy')}>FY {startYear}-{String(endYear).slice(-2)}</button>
                  <button className={`px-3 py-2 text-sm ${period==='custom'?'bg-emerald-600 text-white':'bg-zinc-50'}`} onClick={()=>setPeriod('custom')}>Custom</button>
                </div>
              </div>

              <div className="md:col-start-3">
                <div className="flex items-center justify-between">
                  <Label>Goal</Label>
                  <button className="text-xs underline text-zinc-600" onClick={()=>setGoalsOpen(true)}>Edit monthly goals {overridesCount>0 && <span className="ml-1">({overridesCount} set)</span>}</button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-2 py-1 rounded-lg border border-black dark:border-zinc-700 text-sm">{currency(periodGoal)}</div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Auto-calculated from your monthly goals for the selected period.</div>
              </div>
              <div>
                <Label>Avg job value</Label>
                <div className="flex items-center gap-3">
                  <div className="px-2 py-1 rounded-lg border border-black dark:border-zinc-700 text-sm">{jobsCount>0 ? currency(avgJobValue) : '–'}</div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Jobs needed: <b>{jobs}</b>{jobs==='–' && <span className="text-zinc-400 ml-1">-</span>}</div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Avg based on {jobsCount} {jobsCount===1?'job':'jobs'} in {periodLabel.toLowerCase()} {includeProjected ? '(incl. drafts)' : ''}. Period goal: {currency(periodGoal)}.</div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Profit goal</Label>
                  <button className="text-xs underline text-zinc-600" onClick={()=>setProfitGoalsOpen(true)}>Edit monthly profit goals {Object.keys(profitGoalsByMonth).length>0 && <span className="ml-1">({Object.keys(profitGoalsByMonth).length} set)</span>}</button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-2 py-1 rounded-lg border border-black dark:border-zinc-700 text-sm">{currency(periodProfitGoal)}</div>
                  <div className="text-[11px] text-zinc-500">Profit goal left: <b>{currency(profitGap)}</b></div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Auto-calculated from your monthly profit goals for the selected period.</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
              <Stat title={includeProjected?"Revenue (incl. upcoming)":"Revenue (actual)"} value={currency(revenue)} />
              <Stat title="Actual (invoiced)" value={currency(actual)} />
              <Stat title="Upcoming (drafts)" value={currency(upcoming)} />
              <Stat title="Costs" value={currency(costTotal)} />
              <Card>
  <CardContent>
    <div className="text-xs text-zinc-500">Profit</div>

    <div className="flex items-center justify-between mt-1">
      <div className="text-xl font-semibold">
        {currency(profit)}
      </div>

      <div className="text-sm text-zinc-500">
        {margin.toFixed(1)}%
      </div>
    </div>
  </CardContent>
</Card>
            </div>
          </CardContent>
        </Card>

        {/* LISTS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-orange-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Sales/Invoices – {periodLabel}</div>
                <Button onClick={()=> downloadCSV(filteredInvoices.map(i=>({Date:i.date, Customer:i.customer||'', JobType:i.jobType||'', Total:i.total.toFixed(2), Status:i.status})), `invoices_${todayIso()}.csv`)}>Download CSV</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-2 flex items-center gap-2">
                <Input placeholder="Filter by customer…" value={invoiceFilter} onChange={e=>setInvoiceFilter(e.target.value)} />
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500"><th className="py-2">Date</th><th className="py-2">Customer</th><th className="py-2">Description</th><th className="py-2">Total</th><th className="py-2">Status</th><th className="py-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.sort((a,b)=>a.date<b.date?-1:1).map(i=> (
                      <tr key={i.id} className="border-t border-zinc-100 dark:border-zinc-800">
                        <td className="py-2">{i.date}</td>
                        <td className="py-2">{i.customer || '—'}</td>
                        <td className="py-2">{i.jobType || '—'}</td>
                        <td className="py-2">{currency(i.total)}</td> 
                        <td className="py-2">
  {i.status === "Draft" && (
    <Badge tone="warning">Draft</Badge>
  )}

  {i.status === "Sent" && (
    <Badge tone="default">Sent</Badge>
  )}

  {i.status === "Paid" && (
    <Badge tone="success">Paid</Badge>
  )}
</td>
                        <td className="py-2"><ActionRow record={i} jobTypes={jobTypes} onApply={(rec,scope)=>updateInvoice(rec,scope)} onDelete={(scope)=>deleteInvoice(i,scope)} scoped={!!i.recurrenceId} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-green-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Expenses – {periodLabel}</div>
                <Button onClick={()=> downloadCSV(periodExpenses.map(e=>({Date:e.date, Supplier:e.supplier||'', Total:e.total.toFixed(2), GST:(e.gst||0).toFixed(2)})), `expenses_${todayIso()}.csv`)}>Download CSV</Button>
              </div>
            </CardHeader>
            <CardContent>
            <div className="mb-2 flex items-center gap-2">
             <Input placeholder="Filter by supplier…" value={expenseFilter} onChange={e=>setExpenseFilter(e.target.value)} />
            </div>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500">
                      <th className="py-2">Date</th>
                      <th className="py-2">Supplier</th>
                      <th className="py-2">Total</th>
                      <th className="py-2">GST</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.sort((a,b)=>a.date<b.date?-1:1).map(e=> (
                      <tr key={e.id} className="border-t border-zinc-100 dark:border-zinc-800">
                        <td className="py-2">{e.date}</td>
                        <td className="py-2">{e.supplier || '—'}</td>
                        <td className="py-2">{currency(e.total)}</td>
                        <td className="py-2">{currency(e.gst||0)}</td>

                        <td className="py-2">
                    {(e.status || "Draft") === "Draft" && (
                      <Badge tone="warning">Draft</Badge>
                    )}

                    {(e.status || "Draft") === "Paid" && (
                      <Badge tone="success">Paid</Badge>
                    )}
                    </td>
                        <td className="py-2"><ActionRow record={e} onApply={(rec,scope)=>updateExpense(rec,scope)} onDelete={(scope)=>deleteExpense(e,scope)} scoped={!!e.recurrenceId} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* EXPORT SNAPSHOT */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-zinc-500">Snapshot export includes KPIs for the selected period.</div>
          <Button onClick={()=> downloadCSV(snapshotRows, `forecast_${period}_${todayIso()}.csv`)}>Download snapshot CSV</Button>
        </div>
        {/* BACKUP & RESTORE */}
<section style={{ marginTop: 16, padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
  <h3>Backup & Restore</h3>
  <button
  onClick={exportNow}
  style={{
    padding: '6px 10px',
    border: '1px solid #ccc',
    borderRadius: 6,
    cursor: 'pointer',
    backgroundColor: '#f7f7f7',
    marginRight: 8
  }}
>
  Backup now
</button>

<button
  onClick={importNow}
  style={{
    padding: '6px 10px',
    border: '1px solid #ccc',
    borderRadius: 6,
    cursor: 'pointer',
    backgroundColor: '#f7f7f7'
  }}
>
  Restore backup
</button>
  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>{msg}</div>
</section>

        {/* SELF-TESTS */}
      </div>

      {/* GOALS MODALS */}
      <Modal open={goalsOpen} onClose={()=>setGoalsOpen(false)} title={`Monthly goals for FY ${startYear}-${String(endYear).slice(-2)}`} footer={<>
        <Button onClick={()=>{ setGoalsByMonth({}); }}>Clear overrides</Button>
        <PrimaryButton onClick={()=>setGoalsOpen(false)}>Done</PrimaryButton>
      </>}>
        <div className="text-sm text-zinc-500">Set per-month revenue goals. Unset months fall back to the default monthly goal below.</div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Label>Default monthly goal</Label>
          <input type="number" className="w-32 px-2 py-1 rounded-lg border border-black dark:border-zinc-700 bg-transparent" value={goal}
            onChange={(e)=> setGoal(Number(e.target.value||0))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {monthsBetweenInclusive(fyStart, fyEnd).map((m)=>{
            const key = monthKey(m);
            const val = goalsByMonth[key] ?? '';
            return (
              <div key={key} className="flex items-center justify-between gap-3 border rounded-xl px-3 py-2">
                <div className="text-sm">{labelMonth(m)}</div>
                <div className="flex items-center gap-2">
                  <input type="number" className="w-28 px-2 py-1 rounded-lg border border-black dark:border-zinc-700 bg-transparent" placeholder={String(goal)} value={val}
                    onChange={(e)=>{
                      const num = Number(e.target.value);
                      setGoalsByMonth(prev=>{
                        const next = { ...prev };
                        if (!e.target.value) { delete next[key]; return next; }
                        if (isNaN(num)) return next;
                        next[key] = num; return next;
                      });
                    }}
                  />
                  {goalsByMonth[key] != null && (
                    <button className="text-xs text-zinc-500 underline" onClick={()=> setGoalsByMonth(prev=>{ const n={...prev}; delete n[key]; return n; })}>Clear</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      <Modal open={profitGoalsOpen} onClose={()=>setProfitGoalsOpen(false)} title={`Monthly profit goals for FY ${startYear}-${String(endYear).slice(-2)}`} footer={<>
        <Button onClick={()=>{ setProfitGoalsByMonth({}); }}>Clear overrides</Button>
        <PrimaryButton onClick={()=>setProfitGoalsOpen(false)}>Done</PrimaryButton>
      </>}>
        <div className="text-sm text-zinc-500">Set per-month <b>profit</b> goals. Unset months fall back to the default monthly profit goal below.</div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Label>Default monthly profit goal</Label>
          <input type="number" className="w-32 px-2 py-1 rounded-lg border border-black dark:border-zinc-700 bg-transparent" value={profitGoal}
            onChange={(e)=> setProfitGoal(Number(e.target.value||0))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {monthsBetweenInclusive(fyStart, fyEnd).map((m)=>{
            const key = monthKey(m);
            const val = profitGoalsByMonth[key] ?? '';
            return (
              <div key={key} className="flex items-center justify-between gap-3 border rounded-xl px-3 py-2">
                <div className="text-sm">{labelMonth(m)}</div>
                <div className="flex items-center gap-2">
                  <input type="number" className="w-28 px-2 py-1 rounded-lg border border-black dark:border-zinc-700 bg-transparent" placeholder={String(profitGoal)} value={val}
                    onChange={(e)=>{
                      const num = Number(e.target.value);
                      setProfitGoalsByMonth(prev=>{
                        const next = { ...prev };
                        if (!e.target.value) { delete next[key]; return next; }
                        if (isNaN(num)) return next;
                        next[key] = num; return next;
                      });
                    }}
                  />
                  {profitGoalsByMonth[key] != null && (
                    <button className="text-xs text-zinc-500 underline" onClick={()=> setProfitGoalsByMonth(prev=>{ const n={...prev}; delete n[key]; return n; })}>Clear</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* MODALS */}
{/* --- Invoice Modal --- */}
<Modal
  open={invOpen}
  onClose={() => setInvOpen(false)}
  title="New sale"
  footer={
    <>
      <Button onClick={() => setInvOpen(false)}>
        Cancel
      </Button>

      <PrimaryButton
        onClick={() => {
          addInvoices(invForm, repeatInv);

          setInvOpen(false);
          setInvForm({
            date: todayIso(),
            customer: '',
            jobType: '',
            total: 0,
            status: 'Draft',
          });
          setShowAddJobTypeInv(false);
          setNewJobTypeInv('');
        }}
      >
        Save
      </PrimaryButton>
    </>
  }
>
  {/* Modal body content starts here */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

  <div>
    <Label>Date</Label>
    <Input
      type="date"
      value={invForm.date}
      onChange={e => setInvForm({ ...invForm, date: e.target.value })}
    />
  </div>

  <div>
    <Label>Customer</Label>
<Input
  list="customerList"
  value={invForm.customer}
  onChange={e => setInvForm({ ...invForm, customer: e.target.value })}
  placeholder="Dreamer Inc..."
/>

<datalist id="customerList">
  {customers.map(c => (
    <option key={c} value={c} />
  ))}
</datalist>
  </div>

  <div>
    <Label>Description</Label>
<select
  value={invForm.jobType}
  onChange={e => {
    if (e.target.value === "__add_new__") {
      setShowAddJobTypeInv(true);
      return;
    }
    setInvForm({ ...invForm, jobType: e.target.value });
  }}
  className="w-full px-3 py-2 rounded-xl border border-black"
>
  <option value="">Select Description</option>

  {jobTypes.map(j => (
    <option key={j} value={j}>{j}</option>
  ))}

  <option value="__add_new__">+ Add new Description</option>
</select>

{showAddJobTypeInv && (
  <div className="mt-2 flex gap-2">
    <Input
      placeholder="New description..."
      value={newJobTypeInv}
      onChange={e => setNewJobTypeInv(e.target.value)}
    />

    <Button
      onClick={() => {
        const name = newJobTypeInv.trim();
        if (!name) return;

        setJobTypes(prev =>
          prev.includes(name) ? prev : [...prev, name].sort()
        );

        setInvForm({ ...invForm, jobType: name });
        setNewJobTypeInv('');
        setShowAddJobTypeInv(false);
      }}
    >
      Add
    </Button>
  </div>
)}
  </div>

  <div>
    <Label>Total (incl.)</Label>
    <Input
      type="number"
      value={invForm.total}
      onChange={e => setInvForm({ ...invForm, total: e.target.value })}
    />
  </div>

  <div>
    <Label>GST</Label>
    <Input
      type="number"
      value={invForm.gst}
      onChange={e => setInvForm({ ...invForm, gst: e.target.value })}
    />
  </div>

</div>
<RepeatControls
  repeat={repeatInv}
  setRepeat={setRepeatInv}
  fyEndIso={fyEndIso}
/>
</Modal>

<Modal
  open={expOpen}
  onClose={() => setExpOpen(false)}
  title="New expense"
  footer={
      <PrimaryButton
  onClick={() => {

    // ✅ Save
    addExpenses(expForm, repeatExp);

    // ✅ Reset
    setExpOpen(false);
    setExpForm({
      date: todayIso(),
      supplier: '',
      total: 0,
      gst: 0,
      personal: false,
      status: "Draft"
    });
  }}
>
  Save
</PrimaryButton>
}
>
<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
  <div>
    <Label>Date</Label>
    <Input type="date" value={expForm.date} onChange={e=>setExpForm({...expForm, date:e.target.value})} />
  </div>
  <div>
    <Label>Supplier</Label>
    <Input
    list="supplierList"
    value={expForm.supplier}
    onChange={e => setExpForm({ ...expForm, supplier: e.target.value })}
    placeholder="Bunnings, BP..."
  />
  <datalist id="supplierList">
    {suppliers.map(s => (
      <option key={s} value={s} />
    ))}
  </datalist>
  </div>
    <div>
  <Label>GST</Label>
  <Input
    type="number"
    value={expForm.gst}
    onChange={e => setExpForm({ ...expForm, gst: Number(e.target.value) })}
  />
</div>

<div>
  <Label>Total (incl.)</Label>
  <Input
    type="number"
    value={expForm.total}
    onChange={e =>
      setExpForm({
        ...expForm,
        total: e.target.value
      })
    }
  />
</div>
    <div>
      <Label>Status</Label>
      <Select
        value={expForm.status}
        onChange={e => setExpForm({ ...expForm, status: e.target.value })}
    >
      <option>Draft</option>
      <option>Paid</option>
    </Select>
  </div>

    <div className="flex items-center gap-2 mt-1"><input id="personal" type="checkbox" checked={expForm.personal} onChange={e=>setExpForm({...expForm, personal:e.target.checked})} /><Label>Mark as personal (excluded)</Label></div>
    </div>
    <RepeatControls repeat={repeatExp} setRepeat={setRepeatExp} fyEndIso={fyEndIso} />
  </Modal>
</div>
);
}

import { createRoot } from "react-dom/client";

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}
